import { Checkbox, Field, TextOutput } from 'Components/Core';
import { NamePrefixModifier, withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { ListFieldSection, SectionGroup } from 'Components/Layout';
import { WorksheetEquipmentDropdown } from 'Components/Worksheets/Core';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { getSieveSizeFromId } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/Helpers';
import { SieveSizeId } from 'Data/ViewModels/Equipment/ISieveSize';
import { pipe } from 'Lib/Hooks/Selectors';
import deepEquals from 'Lib/Object/deepEquals';
import React from 'react';
import { useSelector } from 'react-redux';
import AnnexASievingWashConstantDryMassMultiTraySection from './AnnexASievingWashConstantDryMassSectionMultiTray';
import AnnexASievingWashConstantDryMassSingleTraySection from './AnnexASievingWashConstantDryMassSingleTraySection';

let AnnexASievingWashCoarsePortionWashingSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const screeningsPresent = useSelector(pipe(createWorksheetSelector!('Masses.ScreeningsPresent'), (m) => m === true));

  const allSieveSizes = useSelector(createWorksheetSelector!('Equipment.AllSieveSizes'), deepEquals);

  const screeningSieveSize = useSelector(
    pipe(createWorksheetSelector!('Equipment.ScreeningSieveSizeId'), (s: SieveSizeId) =>
      getSieveSizeFromId(s, allSieveSizes)
    )
  );

  const washingSieveSize = useSelector(
    pipe(createWorksheetSelector!('Equipment.WashingSieveSizeId'), (s: SieveSizeId) =>
      getSieveSizeFromId(s, allSieveSizes)
    )
  );

  const screeningSieveSizeCaption = screeningSieveSize?.Caption ?? '16.0 mm';
  const washingSieveSizeCaption = washingSieveSize?.Caption ?? '63.0 µm';

  const singleTray = useSelector(
    pipe(createWorksheetSelector!('Masses.MeasuredWashedConstantDryMass.DryingTrays'), (p) => !p || p.length < 2)
  );

  return (
    <>
      <ListFieldSection>
        <Field input={<TextOutput />} name="Masses.InitialDryMass" label="Initial Coarse Portion Dry Mass" />
        <Field
          input={<WorksheetEquipmentDropdown />}
          equipmentType="oven"
          name="Masses.MeasuredWashedConstantDryMass.Equipment.Oven"
        />
        <Field
          input={<WorksheetEquipmentDropdown />}
          equipmentType="sieve"
          name="Equipment.WashingSieve"
          label={`${washingSieveSizeCaption} Sieve`}
          queryParams={{
            SieveSize: washingSieveSize ? `${washingSieveSize?.Size!.value} ${washingSieveSize?.Size!.uom}` : '63 μm',
          }}
        />
        <Field
          input={<WorksheetEquipmentDropdown />}
          equipmentType="sieve"
          name="Equipment.ScreeningSieve"
          label={`${screeningSieveSizeCaption} Sieve`}
          queryParams={{
            SieveSize: screeningSieveSize
              ? `${screeningSieveSize?.Size!.value} ${screeningSieveSize?.Size!.uom}`
              : '16 mm',
          }}
        />
      </ListFieldSection>

      <NamePrefixModifier appendNamePrefix={'Masses.MeasuredWashedConstantDryMass'}>
        <SectionGroup heading={`Material Retained on ${screeningSieveSizeCaption} Sieve Washed Dry Mass`}>
          {singleTray ? (
            <AnnexASievingWashConstantDryMassSingleTraySection addTrayButtonVissible />
          ) : (
            <AnnexASievingWashConstantDryMassMultiTraySection />
          )}
        </SectionGroup>
      </NamePrefixModifier>
      <Field input={<TextOutput />} name="Masses.WashedDryMass" label="Coarse Portion Washed Dry Mass" />

      <Field
        input={<Checkbox />}
        name="Masses.ScreeningsPresent"
        label={`Material exists between ${washingSieveSizeCaption} and ${screeningSieveSizeCaption} sieves`}
      />

      {screeningsPresent && (
        <>
          <NamePrefixModifier appendNamePrefix={'Masses.MeasuredScreeningsConstantDryMass'}>
            <SectionGroup
              heading={`Material Between ${washingSieveSizeCaption} and ${screeningSieveSizeCaption} Sieve`}
            >
              <AnnexASievingWashConstantDryMassSingleTraySection />
            </SectionGroup>
          </NamePrefixModifier>

          <ListFieldSection>
            <Field
              input={<TextOutput />}
              name="Masses.ScreeningsDryMass"
              label={`Material Between ${washingSieveSizeCaption} and ${screeningSieveSizeCaption} Sieves Dry Mass`}
            />
            <Field input={<TextOutput />} name="Masses.WashingsDryMass" />
          </ListFieldSection>
        </>
      )}
    </>
  );
};

AnnexASievingWashCoarsePortionWashingSection = withInjectedProps(AnnexASievingWashCoarsePortionWashingSection);

export default AnnexASievingWashCoarsePortionWashingSection;
export { AnnexASievingWashCoarsePortionWashingSection as AnnexACoarsePortionWashingSection };
